// src/views/Reports/StaffStatisticsReport.jsx
import { useEffect, useMemo, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Table from "../../components/Tables/Table";
import TableHeader from "../../components/Tables/TableHeader";
import { Select } from "antd";
import toast from "react-hot-toast";
import { getStaffStatistics } from "../../services/staffStatisticsService";
import { getInstitutions } from "../../services/institutionService";
import { getSessions } from "../../services/sessionService";
import DownloadButton from "../../components/Buttons/DownloadButton";
import { generatePDF } from "../../utils/pdf";
import { utils, writeFile } from "xlsx";

const { Option } = Select;

const StaffStatisticsReport = () => {
  const [stats, setStats] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [institutions, setInstitutions] = useState([]);
  const [sessions, setSessions] = useState([]);

  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");
  const [selectedSession, setSelectedSession] = useState("Not Selected");

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedRowKey, setExpandedRowKey] = useState(null);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [staffRes, instRes, sessRes] = await Promise.all([
        getStaffStatistics(),
        getInstitutions(),
        getSessions(),
      ]);
      const rows = staffRes?.data?.data || [];
      setStats(rows);
      setFiltered(rows);
      setInstitutions(instRes?.data?.data || []);
      setSessions(sessRes?.data?.data || []);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Filters
  useEffect(() => {
    let f = stats;

    if (searchTerm.trim() !== "") {
      f = f.filter((item) =>
        (item.institution?.name || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      f = f.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution?.toString()
      );
    }

    if (selectedSession !== "Not Selected") {
      f = f.filter(
        (item) => item.session?.id?.toString() === selectedSession?.toString()
      );
    }

    setFiltered(f);
  }, [searchTerm, selectedInstitution, selectedSession, stats]);

  // Group by Institution + Session
  const grouped = useMemo(() => {
    const map = new Map();
    for (const item of filtered) {
      const inst = item.institution;
      const sess = item.session;
      if (!inst || !sess) continue;
      const key = `${inst.id}__${sess.id}`;

      if (!map.has(key)) {
        map.set(key, {
          key,
          institution: inst,
          session: sess,
          data: [],
          totals: {
            phd_full: 0,
            phd_part: 0,
            phd_retained: 0,
            prof_full: 0,
            prof_part: 0,
            prof_retained: 0,
            msc_full: 0,
            msc_part: 0,
            msc_retained: 0,
            total: 0,
          },
        });
      }

      const grp = map.get(key);
      grp.data.push(item);

      const phdF = item.phd_staff_count_full_time || 0;
      const phdP = item.phd_staff_count_part_time || 0;
      const phdR = item.phd_staff_count_retained || 0;

      const profF = item.professor_staff_count_full_time || 0;
      const profP = item.professor_staff_count_part_time || 0;
      const profR = item.professor_staff_count_retained || 0;

      const mscF = item.masters_staff_count_full_time || 0;
      const mscP = item.masters_staff_count_part_time || 0;
      const mscR = item.masters_staff_count_retained || 0;

      grp.totals.phd_full += phdF;
      grp.totals.phd_part += phdP;
      grp.totals.phd_retained += phdR;

      grp.totals.prof_full += profF;
      grp.totals.prof_part += profP;
      grp.totals.prof_retained += profR;

      grp.totals.msc_full += mscF;
      grp.totals.msc_part += mscP;
      grp.totals.msc_retained += mscR;

      grp.totals.total +=
        phdF + phdP + phdR + profF + profP + profR + mscF + mscP + mscR;
    }
    return Array.from(map.values());
  }, [filtered]);

  // PDF export
  const handleDownloadPDF = () => {
    const title = "Staff Statistics Report";
    const fileName = "staff-statistics-report.pdf";

    const columns = [
      "Institution",
      "Year",
      "PhD FT",
      "PhD PT",
      "PhD Retained",
      "Professor FT",
      "Professor PT",
      "Professor Retained",
      "Masters FT",
      "Masters PT",
      "Masters Retained",
      "Total",
    ];

    const rows = [];

    grouped.forEach((group) => {
      // group summary row
      rows.push([
        group.institution?.name || "N/A",
        group.session?.name || "N/A",
        group.totals.phd_full,
        group.totals.phd_part,
        group.totals.phd_retained,
        group.totals.prof_full,
        group.totals.prof_part,
        group.totals.prof_retained,
        group.totals.msc_full,
        group.totals.msc_part,
        group.totals.msc_retained,
        group.totals.total,
      ]);

      // detail rows
      group.data.forEach((item) => {
        const rowTotal =
          (item.phd_staff_count_full_time || 0) +
          (item.phd_staff_count_part_time || 0) +
          (item.phd_staff_count_retained || 0) +
          (item.professor_staff_count_full_time || 0) +
          (item.professor_staff_count_part_time || 0) +
          (item.professor_staff_count_retained || 0) +
          (item.masters_staff_count_full_time || 0) +
          (item.masters_staff_count_part_time || 0) +
          (item.masters_staff_count_retained || 0);

        rows.push([
          `-> ${item.focus_area?.name || "N/A"}`,
          "", // indent under Institution/Year
          item.phd_staff_count_full_time ?? 0,
          item.phd_staff_count_part_time ?? 0,
          item.phd_staff_count_retained ?? 0,
          item.professor_staff_count_full_time ?? 0,
          item.professor_staff_count_part_time ?? 0,
          item.professor_staff_count_retained ?? 0,
          item.masters_staff_count_full_time ?? 0,
          item.masters_staff_count_part_time ?? 0,
          item.masters_staff_count_retained ?? 0,
          rowTotal,
        ]);
      });

      // spacer
      rows.push(["", "", "", "", "", "", "", "", "", "", "", ""]);
    });

    generatePDF({ title, columns, rows, fileName, orientation: "landscape" });
  };

  // Excel export
  const handleDownloadExcel = () => {
    // --- Summary sheet (totals per Institution+Year) ---
    const summaryCols = [
      "Institution",
      "Year",
      "PhD FT",
      "PhD PT",
      "PhD Retained",
      "Professor FT",
      "Professor PT",
      "Professor Retained",
      "Masters FT",
      "Masters PT",
      "Masters Retained",
      "Total",
    ];

    const summaryRows = grouped.map((g) => [
      g.institution?.name || "N/A",
      g.session?.name || "N/A",
      g.totals.phd_full,
      g.totals.phd_part,
      g.totals.phd_retained,
      g.totals.prof_full,
      g.totals.prof_part,
      g.totals.prof_retained,
      g.totals.msc_full,
      g.totals.msc_part,
      g.totals.msc_retained,
      g.totals.total,
    ]);

    // --- Details sheet (each focus area row) ---
    const detailsCols = [
      "Institution",
      "Year",
      "Focus Area",
      "PhD FT",
      "PhD PT",
      "PhD Retained",
      "Professor FT",
      "Professor PT",
      "Professor Retained",
      "Masters FT",
      "Masters PT",
      "Masters Retained",
      "Total",
    ];

    const detailsRows = [];
    grouped.forEach((g) => {
      g.data.forEach((item) => {
        const rowTotal =
          (item.phd_staff_count_full_time || 0) +
          (item.phd_staff_count_part_time || 0) +
          (item.phd_staff_count_retained || 0) +
          (item.professor_staff_count_full_time || 0) +
          (item.professor_staff_count_part_time || 0) +
          (item.professor_staff_count_retained || 0) +
          (item.masters_staff_count_full_time || 0) +
          (item.masters_staff_count_part_time || 0) +
          (item.masters_staff_count_retained || 0);

        detailsRows.push([
          g.institution?.name || "N/A",
          g.session?.name || "N/A",
          item.focus_area?.name || "N/A",
          item.phd_staff_count_full_time ?? 0,
          item.phd_staff_count_part_time ?? 0,
          item.phd_staff_count_retained ?? 0,
          item.professor_staff_count_full_time ?? 0,
          item.professor_staff_count_part_time ?? 0,
          item.professor_staff_count_retained ?? 0,
          item.masters_staff_count_full_time ?? 0,
          item.masters_staff_count_part_time ?? 0,
          item.masters_staff_count_retained ?? 0,
          rowTotal,
        ]);
      });
    });

    // Build workbook
    const wb = utils.book_new();

    const wsSummary = utils.aoa_to_sheet([summaryCols, ...summaryRows]);
    utils.book_append_sheet(wb, wsSummary, "Summary");

    const wsDetails = utils.aoa_to_sheet([detailsCols, ...detailsRows]);
    utils.book_append_sheet(wb, wsDetails, "Details");

    writeFile(wb, "staff-statistics-report.xlsx");
  };

  const columns = [
    {
      title: "Institution",
      dataIndex: "institution",
      width: 260,
      render: (institution) => (
        <span className="font-medium text-gray-800">{institution?.name}</span>
      ),
    },
    {
      title: "Year",
      dataIndex: ["session", "name"],
      width: 120,
    },
    { title: "PhD FT", dataIndex: ["totals", "phd_full"], width: 100 },
    { title: "PhD PT", dataIndex: ["totals", "phd_part"], width: 100 },
    {
      title: "PhD Retained",
      dataIndex: ["totals", "phd_retained"],
      width: 120,
    },
    { title: "Professor FT", dataIndex: ["totals", "prof_full"], width: 120 },
    { title: "Professor PT", dataIndex: ["totals", "prof_part"], width: 120 },
    {
      title: "Professor Retained",
      dataIndex: ["totals", "prof_retained"],
      width: 150,
    },
    { title: "Masters FT", dataIndex: ["totals", "msc_full"], width: 110 },
    { title: "Masters PT", dataIndex: ["totals", "msc_part"], width: 110 },
    {
      title: "Masters Retained",
      dataIndex: ["totals", "msc_retained"],
      width: 150,
    },
    { title: "Total", dataIndex: ["totals", "total"], width: 120 },
  ];

  return (
    <>
      <PageHeading title="Staff Statistics Report" subtitle1="Reports" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Staff Statistics Report"
            count={grouped.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            searchAdd={false}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={grouped}
            expandable={{
              expandedRowKeys: expandedRowKey ? [expandedRowKey] : [],
              onExpand: (expanded, record) =>
                setExpandedRowKey(expanded ? record.key : null),
              expandedRowRender: (record) => (
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-2">Focus Area</th>
                      <th className="text-left p-2">PhD FT</th>
                      <th className="text-left p-2">PhD PT</th>
                      <th className="text-left p-2">PhD Retained</th>
                      <th className="text-left p-2">Professor FT</th>
                      <th className="text-left p-2">Professor PT</th>
                      <th className="text-left p-2">Professor Retained</th>
                      <th className="text-left p-2">Masters FT</th>
                      <th className="text-left p-2">Masters PT</th>
                      <th className="text-left p-2">Masters Retained</th>
                      <th className="text-left p-2">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-50">
                    {record.data.map((item) => {
                      const rowTotal =
                        (item.phd_staff_count_full_time || 0) +
                        (item.phd_staff_count_part_time || 0) +
                        (item.phd_staff_count_retained || 0) +
                        (item.professor_staff_count_full_time || 0) +
                        (item.professor_staff_count_part_time || 0) +
                        (item.professor_staff_count_retained || 0) +
                        (item.masters_staff_count_full_time || 0) +
                        (item.masters_staff_count_part_time || 0) +
                        (item.masters_staff_count_retained || 0);

                      return (
                        <tr key={item.id}>
                          <td className="p-2">
                            {item.focus_area?.name || "N/A"}
                          </td>
                          <td className="p-2">
                            {item.phd_staff_count_full_time ?? 0}
                          </td>
                          <td className="p-2">
                            {item.phd_staff_count_part_time ?? 0}
                          </td>
                          <td className="p-2">
                            {item.phd_staff_count_retained ?? 0}
                          </td>

                          <td className="p-2">
                            {item.professor_staff_count_full_time ?? 0}
                          </td>
                          <td className="p-2">
                            {item.professor_staff_count_part_time ?? 0}
                          </td>
                          <td className="p-2">
                            {item.professor_staff_count_retained ?? 0}
                          </td>

                          <td className="p-2">
                            {item.masters_staff_count_full_time ?? 0}
                          </td>
                          <td className="p-2">
                            {item.masters_staff_count_part_time ?? 0}
                          </td>
                          <td className="p-2">
                            {item.masters_staff_count_retained ?? 0}
                          </td>

                          <td className="p-2">{rowTotal}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ),
            }}
            extra={
              <div className="mb-2 py-2 flex gap-2">
                <Select
                  defaultValue={selectedInstitution}
                  className="w-full h-[36px] min-w-[250px]"
                  onChange={(value) => setSelectedInstitution(value)}
                  showSearch
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option value="Not Selected">Filter by Institution</Option>
                  {institutions.map((inst) => (
                    <Option key={inst.id} value={inst.id}>
                      {inst.name}
                    </Option>
                  ))}
                </Select>

                <Select
                  defaultValue={selectedSession}
                  className="w-full h-[36px] min-w-[250px]"
                  onChange={(value) => setSelectedSession(value)}
                  showSearch
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option value="Not Selected">Filter by Year</Option>
                  {sessions.map((s) => (
                    <Option key={s.id} value={s.id}>
                      {s.name}
                    </Option>
                  ))}
                </Select>
              </div>
            }
          />

          <div className="mt-4 flex gap-2 flex-wrap">
            <DownloadButton
              onDownload={handleDownloadPDF}
              title="Download PDF"
            />
            <DownloadButton
              onDownload={handleDownloadExcel}
              title="Download Excel"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default StaffStatisticsReport;
